// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
// Searchable client picker.
//
// A combobox that replaces every "select a client" <select> in the admin
// portal. The reason it exists: as soon as a firm has more than ~30
// clients the native dropdown becomes useless — alphabetized differently
// on every OS, hard to scan, and impossible to find anyone by anything
// other than starting-letter. This component supports:
//
//   * Free-text filter across name, email, matter number, case type
//   * Keyboard nav (↑ ↓ to move, Enter to pick, Esc to clear)
//   * Clear button to reset selection
//   * Subtle second line on each row showing matter # / case type / status
//     so you can pick the right "John Smith" among several
//
// Usage:
//   <ClientPicker
//     value={clientId}
//     onChange={setClientId}
//     statusFilter="all"      // or 'active' / 'closed' / etc — passes to listClients
//     disabled={running}
//     placeholder="Type a name..."
//   />
//
// Notes:
//   * Loads the full client list once on mount (statusFilter respected),
//     then filters in JS — same call as the old dropdown, no extra load.
//   * If the list is huge (>2000) it still works; we cap the dropdown to
//     the top 50 filtered results to keep DOM cheap.

import React, { useEffect, useRef, useState } from 'react';
import { adminApi } from '../utils/adminApi';

const MAX_VISIBLE = 50;

export default function ClientPicker({
  value,
  onChange,
  statusFilter = 'all',
  disabled = false,
  placeholder = 'Search by name, email, or matter number…',
  autoFocus = false,
}) {
  const [allClients, setAllClients] = useState([]);
  const [loaded, setLoaded]         = useState(false);
  const [loadError, setLoadError]   = useState('');
  const [query, setQuery]           = useState('');
  const [open, setOpen]             = useState(false);
  const [highlight, setHighlight]   = useState(0);

  const inputRef     = useRef(null);
  const containerRef = useRef(null);
  const listRef      = useRef(null);

  // ── Load client list once on mount ───────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    adminApi.listClients(statusFilter)
      .then(rows => { if (!cancelled) { setAllClients(rows || []); setLoaded(true); } })
      .catch(err  => { if (!cancelled) { setLoadError(err.message); setLoaded(true); } });
    return () => { cancelled = true; };
  }, [statusFilter]);

  // ── If parent passes in a value, populate the input with that client's name.
  // We only do this on mount or when value changes from outside. Don't
  // overwrite while the user is actively typing.
  useEffect(() => {
    if (!value) {
      // Cleared from outside.
      if (!open) setQuery('');
      return;
    }
    if (!loaded) return;
    const selected = allClients.find(c => c.id === value);
    if (selected && !open) setQuery(selected.name);
    // Note: `open` is intentionally a dep here — we re-check `!open` after
    // it changes so we don't overwrite the user's typing. The effect is
    // idempotent on re-runs with the same selected client.
  }, [value, loaded, allClients, open]);

  // ── Close on outside click ───────────────────────────────────────────────
  useEffect(() => {
    function onDocClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // ── Filter results ───────────────────────────────────────────────────────
  const q = query.trim().toLowerCase();
  const selectedClient = value ? allClients.find(c => c.id === value) : null;

  // If the input value exactly matches the selected client's name (because
  // the parent gave us a value and we filled in the name), don't filter
  // anything out — show the full list when the user reopens the dropdown.
  const isShowingAll = !q || (selectedClient && q === selectedClient.name.toLowerCase());

  const filtered = isShowingAll
    ? allClients
    : allClients.filter(c => matches(c, q));

  const visible = filtered.slice(0, MAX_VISIBLE);

  // Reset highlight when filter changes.
  useEffect(() => { setHighlight(0); }, [query, open]);

  // ── Keyboard navigation ──────────────────────────────────────────────────
  function handleKeyDown(e) {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setOpen(true);
      e.preventDefault();
      return;
    }
    if (!open) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = Math.min(highlight + 1, visible.length - 1);
      setHighlight(next);
      scrollHighlightIntoView(listRef, next);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const next = Math.max(highlight - 1, 0);
      setHighlight(next);
      scrollHighlightIntoView(listRef, next);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = visible[highlight];
      if (pick) handlePick(pick);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      inputRef.current?.blur();
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  }

  function handlePick(client) {
    onChange(client.id);
    setQuery(client.name);
    setOpen(false);
  }

  function handleClear() {
    onChange('');
    setQuery('');
    setOpen(false);
    inputRef.current?.focus();
  }

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div ref={containerRef} style={{ position: 'relative', maxWidth: 480 }}>
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          type="text"
          value={query}
          placeholder={loaded ? placeholder : 'Loading clients…'}
          disabled={disabled || !loaded}
          autoFocus={autoFocus}
          autoComplete="off"
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls="client-picker-list"
          style={{
            width: '100%',
            paddingRight: value ? 32 : 12, // make room for the × button
          }}
        />
        {value && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            title="Clear selection"
            aria-label="Clear selection"
            style={{
              position: 'absolute',
              right: 6,
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'transparent',
              border: 'none',
              fontSize: 16,
              cursor: 'pointer',
              color: '#888',
              padding: '2px 6px',
              lineHeight: 1,
            }}
          >
            ×
          </button>
        )}
      </div>

      {loadError && (
        <div style={{ fontSize: 11, color: '#B85042', marginTop: 4 }}>
          Could not load clients: {loadError}
        </div>
      )}

      {open && loaded && (
        <div
          ref={listRef}
          id="client-picker-list"
          role="listbox"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            maxHeight: 320,
            overflowY: 'auto',
            background: 'white',
            border: '1px solid #ccc',
            borderRadius: 4,
            marginTop: 2,
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
            zIndex: 50,
          }}
        >
          {visible.length === 0 ? (
            <div style={{ padding: '12px 14px', fontSize: 13, color: '#888', fontFamily: 'Arial, sans-serif' }}>
              No clients match &quot;{query}&quot;.
            </div>
          ) : (
            <>
              {filtered.length > MAX_VISIBLE && (
                <div style={{ padding: '6px 14px', fontSize: 11, color: '#888', fontFamily: 'Arial, sans-serif', background: '#fafafa', borderBottom: '1px solid #eee' }}>
                  Showing first {MAX_VISIBLE} of {filtered.length.toLocaleString()} matches — keep typing to narrow.
                </div>
              )}
              {visible.map((c, idx) => (
                <ClientRow
                  key={c.id}
                  client={c}
                  highlighted={idx === highlight}
                  selected={c.id === value}
                  query={q}
                  onClick={() => handlePick(c)}
                  onMouseEnter={() => setHighlight(idx)}
                />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function ClientRow({ client, highlighted, selected, query, onClick, onMouseEnter }) {
  const secondLineParts = [];
  if (client.clio_matter_number) secondLineParts.push(`#${client.clio_matter_number}`);
  if (client.case_type)          secondLineParts.push(client.case_type);
  if (client.email)              secondLineParts.push(client.email);

  const statusBadge = client.case_status && client.case_status !== 'active'
    ? client.case_status
    : null;

  return (
    <div
      role="option"
      aria-selected={selected}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      style={{
        padding: '8px 14px',
        cursor: 'pointer',
        background: highlighted ? '#f0f5ff' : 'white',
        borderBottom: '1px solid #f0f0f0',
        fontFamily: 'Arial, sans-serif',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#0F1838' }}>
          {highlightMatch(client.name, query)}
        </div>
        {statusBadge && (
          <span style={{
            display: 'inline-block',
            padding: '1px 7px',
            fontSize: 10,
            background: statusBadge === 'closed' ? '#f0e7d4' : '#e6f0e7',
            color: statusBadge === 'closed' ? '#8a6510' : '#1a6a36',
            borderRadius: 8,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}>{statusBadge}</span>
        )}
      </div>
      {secondLineParts.length > 0 && (
        <div style={{ fontSize: 11, color: '#777', marginTop: 2 }}>
          {secondLineParts.join(' • ')}
        </div>
      )}
    </div>
  );
}

// Plain-text highlight of the matched substring(s). Splits the label
// around occurrences of `query` (case-insensitive) and wraps them in
// <mark>. Returns a single React fragment.
function highlightMatch(label, query) {
  if (!query) return label;
  const q = query.toLowerCase();
  const text = String(label || '');
  const lower = text.toLowerCase();
  const parts = [];
  let i = 0;
  while (i < text.length) {
    const idx = lower.indexOf(q, i);
    if (idx === -1) {
      parts.push(text.slice(i));
      break;
    }
    if (idx > i) parts.push(text.slice(i, idx));
    parts.push(
      <mark key={idx} style={{ background: '#fff3a8', color: 'inherit', padding: 0 }}>
        {text.slice(idx, idx + q.length)}
      </mark>
    );
    i = idx + q.length;
  }
  return <>{parts}</>;
}

// Search a client by name, email, matter number, or case type.
function matches(client, q) {
  const name   = (client.name   || '').toLowerCase();
  const email  = (client.email  || '').toLowerCase();
  const matter = String(client.clio_matter_number || '').toLowerCase();
  const ctype  = (client.case_type || '').toLowerCase();
  // Multi-token search: every space-separated token must appear in at least
  // one field. Lets you type "smith family" and find "Jane Smith - Family Law".
  const tokens = q.split(/\s+/).filter(Boolean);
  return tokens.every(t => name.includes(t) || email.includes(t) || matter.includes(t) || ctype.includes(t));
}

function scrollHighlightIntoView(listRef, newIdx) {
  // Defer to next frame so React has rendered the new highlight before we
  // try to scroll. The first .listbox child is the optional "Showing first N"
  // header, so options live at index newIdx + N where N may be 0 or 1.
  requestAnimationFrame(() => {
    const list = listRef.current;
    if (!list) return;
    const options = list.querySelectorAll('[role="option"]');
    const el = options[newIdx];
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  });
}
